// ===========================================
// Email Branding API Client
// ===========================================
// API client for managing school email branding settings

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export interface EmailBrandingSettings {
  useSchoolLogo: boolean;
  useSchoolColors: boolean;
  emailHeaderColor: string | null;
  emailButtonColor: string | null;
  emailPageBackgroundColor: string | null;
  emailContentBackgroundColor: string | null;
  emailTextColor: string | null;
  emailLinkColor: string | null;
  emailFooterTextColor: string | null;
  emailFromName: string | null;
  emailFooterText: string | null;
  emailSignature: string | null;
  enabledTemplates: {
    [key: string]: boolean;
  };
  customSubjects?: {
    [key: string]: string;
  };
  customPreheaders?: {
    [key: string]: string;
  };
}

export interface EmailBrandingPreview {
  schoolName: string;
  fromName: string;
  logoUrl?: string;
  headerColor: string;
  buttonColor: string;
  pageBackgroundColor: string;
  contentBackgroundColor: string;
  textColor: string;
  linkColor: string;
  footerTextColor: string;
  footerText: string;
  signature?: string;
  websiteUrl?: string;
}

export interface EmailTemplate {
  key: string;
  name: string;
  category: string;
  enabled: boolean;
  description?: string;
  supportsCustomBody?: boolean;
  isSubjectCustomized?: boolean;
  isPreheaderCustomized?: boolean;
  isBodyCustomized?: boolean;
  /**
   * A reminder STEP row (#372), keyed `step:<id>`: its wording and its on/off switch are the step's.
   * `kind` is the step kind, `dayOffset` its days from the due date (<0 before, 0 on, >0 after).
   */
  stepId?: string;
  kind?: 'BEFORE_DUE' | 'ON_DUE' | 'LAST_CHANCE' | 'OVERDUE';
  dayOffset?: number;
}

export interface MergeField {
  key: string;
  placeholder: string;
  label: string;
  description: string;
  example: string;
  required: boolean;
  /**
   * A designed layout section (#315: the amount box, the Pay button): HTML the server renders at
   * send time. Body only, on a line of its own; never offered for a subject or preheader.
   */
  block?: boolean;
  /** A block's real HTML sample (previews and test sends). */
  exampleHtml?: string;
  /** #372: warn when a saved custom body lacks this field, only while the school's late fees are on. */
  warnWhenMissing?: 'lateFees';
}

export interface TemplateMetadata {
  key: string;
  name: string;
  category: string;
  description: string;
  defaultSubject: string;
  defaultPreheader: string;
  defaultBody?: string;
  supportsCustomBody: boolean;
  mergeFields: MergeField[];
}

export interface TemplateCustomization {
  templateKey: string;
  metadata: TemplateMetadata;
  subject: string | null;
  preheader: string | null;
  body: { html: string; createdAt: string; updatedAt: string } | null;
  isSubjectCustomized: boolean;
  isPreheaderCustomized: boolean;
  isBodyCustomized: boolean;
  /** #372: facts that make a field matter now. lateFees = the school has late fees on. Absent on older servers. */
  conditionalFields?: { lateFees: boolean };
}

export interface PreviewResult {
  templateKey: string;
  contentType: 'subject' | 'preheader' | 'body';
  rendered: string;
  isCustom: boolean;
}

export interface FullEmailPreview {
  templateKey: string;
  templateName: string;
  subject: string;
  preheader: string;
  html: string;
}

// ===========================================
// API FUNCTIONS
// ===========================================

/**
 * Get current email branding settings
 */
export async function getEmailBrandingSettings(): Promise<EmailBrandingSettings> {
  return apiClient.get<EmailBrandingSettings>('/admin/settings/email-branding');
}

/**
 * Get preview of current email branding
 */
export async function getEmailBrandingPreview(): Promise<EmailBrandingPreview> {
  return apiClient.get<EmailBrandingPreview>('/admin/settings/email-branding/preview');
}

/**
 * Update email branding settings
 */
export async function updateEmailBranding(
  updates: Partial<EmailBrandingSettings>
): Promise<EmailBrandingSettings> {
  return apiClient.put<EmailBrandingSettings>('/admin/settings/email-branding', updates);
}

/**
 * Reset email branding to defaults
 */
export async function resetEmailBranding(): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>('/admin/settings/email-branding/reset');
}

/**
 * Send test email with current branding
 */
export async function sendTestEmail(templateType: string = 'welcome'): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>('/admin/settings/email-branding/test', { templateType });
}

/**
 * Get list of all email templates
 */
export async function getEmailTemplates(): Promise<EmailTemplate[]> {
  return apiClient.get<EmailTemplate[]>('/admin/settings/email-branding/templates');
}

/**
 * Toggle email template enabled status
 */
export async function toggleEmailTemplate(
  templateKey: string,
  enabled: boolean
): Promise<{ message: string }> {
  return apiClient.put<{ message: string }>(`/admin/settings/email-branding/templates/${templateKey}/toggle`, {
    enabled,
  });
}

/**
 * Get full customization details for a template
 */
export async function getTemplateCustomization(templateKey: string): Promise<TemplateCustomization> {
  return apiClient.get<TemplateCustomization>(
    `/admin/settings/email-branding/templates/${templateKey}/customization`
  );
}

/**
 * Set custom subject line for a template
 */
export async function setCustomSubject(
  templateKey: string,
  subject: string
): Promise<{ message: string; subject: string }> {
  return apiClient.put<{ message: string; subject: string }>(
    `/admin/settings/email-branding/templates/${templateKey}/subject`,
    { subject }
  );
}

/**
 * Reset subject to default for a template
 */
export async function resetCustomSubject(templateKey: string): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(
    `/admin/settings/email-branding/templates/${templateKey}/subject`
  );
}

/**
 * Set custom preheader for a template
 */
export async function setCustomPreheader(
  templateKey: string,
  preheader: string
): Promise<{ message: string; preheader: string }> {
  return apiClient.put<{ message: string; preheader: string }>(
    `/admin/settings/email-branding/templates/${templateKey}/preheader`,
    { preheader }
  );
}

/**
 * Reset preheader to default for a template
 */
export async function resetCustomPreheader(templateKey: string): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(
    `/admin/settings/email-branding/templates/${templateKey}/preheader`
  );
}

/**
 * Generate preview for a template
 */
export async function generateTemplatePreview(
  templateKey: string,
  contentType: 'subject' | 'preheader' | 'body'
): Promise<PreviewResult> {
  return apiClient.post<PreviewResult>(
    `/admin/settings/email-branding/templates/${templateKey}/preview`,
    { contentType }
  );
}

/**
 * Set custom body for a template
 */
export async function setCustomBody(
  templateKey: string,
  html: string
): Promise<{ message: string; body: { html: string; createdAt: string; updatedAt: string } }> {
  return apiClient.put<{ message: string; body: { html: string; createdAt: string; updatedAt: string } }>(
    `/admin/settings/email-branding/templates/${templateKey}/body`,
    { html }
  );
}

/**
 * Reset body to default for a template
 */
export async function resetCustomBody(templateKey: string): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(
    `/admin/settings/email-branding/templates/${templateKey}/body`
  );
}

/**
 * Send a test of a system email to the signed-in admin's own address, filled with sample
 * details. Pass the editor's unsaved fields to test them before saving; omit to test what is saved.
 */
export async function sendTemplateTestEmail(
  templateKey: string,
  draft: { subject?: string; preheader?: string; bodyHtml?: string } = {}
): Promise<{ message: string; to: string }> {
  return apiClient.post<{ message: string; to: string }>(
    `/admin/settings/email-branding/templates/${templateKey}/send-test`,
    draft
  );
}

/**
 * Get full email preview with branding
 */
export async function getFullEmailPreview(templateKey: string): Promise<FullEmailPreview> {
  return apiClient.get<FullEmailPreview>(
    `/admin/settings/email-branding/templates/${templateKey}/full-preview`
  );
}
